import { useState, useEffect } from "react";

import {
  Button,
  Modal,
  CloseSVG,
  Checkbox,
  useModal,
  ChevronLeftSVG,
  Icon,
  DotFilledSVG,
} from "@newsela/angelou";

import {
  checkboxCss,
  closeButtonCss,
  footerButtonsWrapper,
  footerNextButton,
  modalContentImage,
  modalContentImageWrapper,
  modalPagination,
  modalContentText,
  modalContentWrapper,
  modalCss,
  modalFooterCss,
  modalTitle,
  modalWrapper,
  tittleWrapper,
  modalPaginationWrapper,
} from "./GuidedHighlightingTeacherInfoPopup.style";

interface IProps {
  active: boolean;
  setActive: (active: boolean) => void;
  onClickGetStarted: () => void;
}

type PageIndex = 0 | 1 | 2;

export interface IGuidedHighlightingPopupPages {
  page: PageIndex;
  title: string;
  image_url: string;
  heading_text: string;
  paragraph_text: string;
}

export const GUIDED_HIGHLIGHTING_POPUP_PAGES: [
  IGuidedHighlightingPopupPages,
  IGuidedHighlightingPopupPages,
  IGuidedHighlightingPopupPages,
] = [
  {
    page: 0,
    title: "Welcome",
    image_url: "/images/guided-highlights/welcome_illustration.svg",
    heading_text: "Get started with guided highlighting!",
    paragraph_text:
      "Have students practice reading strategies with skill-based, AI-generated prompts that ask them to highlight key parts of the text.",
  },
  {
    page: 1,
    title: "Instructions",
    image_url: "/images/guided-highlights/instructions_illustration.svg",
    heading_text: "Assign skill-based prompts",
    paragraph_text:
      "Assign two prompts for your students to complete. Students will get two chances to highlight the correct passage for each prompt. After two attempts, students will be shown the correct highlighted text.",
  },
  {
    page: 2,
    title: "Instructions",
    image_url: "/images/guided-highlights/instructions_check_illustration.svg",
    heading_text: "Check their performance",
    paragraph_text:
      "To see how your students did, view their work in the grading section of the assignment page.",
  },
];

const GuidedHighlightingTeacherInfoPopup = ({
  active,
  setActive,
  onClickGetStarted,
}: IProps) => {
  const [modalProps] = useModal(true);
  const [currentPage, setCurrentPage] = useState(
    GUIDED_HIGHLIGHTING_POPUP_PAGES[0],
  );
  const [showModal, setShowModal] = useState(true);
  const firstPage = GUIDED_HIGHLIGHTING_POPUP_PAGES[0];
  const lastPage =
    GUIDED_HIGHLIGHTING_POPUP_PAGES[GUIDED_HIGHLIGHTING_POPUP_PAGES.length - 1];

  useEffect(() => {
    const storagedShowModal = localStorage.getItem(
      "skip_teacherguigedhighlighting_popup",
    );
    storagedShowModal ? setShowModal(false) : setShowModal(true);
  }, []);

  useEffect(() => {
    if (!showModal && active) {
      onClickGetStarted();
    }
  }, [active]);

  const handleNextPage = () => {
    if (currentPage?.page !== lastPage?.page) {
      const activePagePosition = currentPage?.page || 0;
      const activePage = activePagePosition + 1;
      const nextPage = GUIDED_HIGHLIGHTING_POPUP_PAGES[activePage];
      if (nextPage) {
        setCurrentPage(nextPage);
      }
    }
  };

  const handleBackPage = () => {
    if (currentPage?.page !== firstPage?.page) {
      const activePagePosition = currentPage?.page || 1;
      const activePage = activePagePosition - 1;
      const previousPage = GUIDED_HIGHLIGHTING_POPUP_PAGES[activePage];
      if (previousPage) {
        setCurrentPage(previousPage);
      }
    }
  };

  const renderPagination = () => {
    return GUIDED_HIGHLIGHTING_POPUP_PAGES.map((page) => (
      <div
        css={modalPagination(page.page === currentPage?.page)}
        key={page.page}
      >
        <Icon
          isDecorative={false}
          size="MD"
          SvgComponent={DotFilledSVG}
          title="Pagination dot"
        />
      </div>
    ));
  };

  const setDoNotShowAgain = (event: { target: { checked: boolean } }) => {
    if (event.target.checked) {
      localStorage.setItem("skip_teacherguigedhighlighting_popup", "true");
    } else {
      localStorage.removeItem("skip_teacherguigedhighlighting_popup");
    }
  };

  const closePopup = () => {
    setShowModal(false);
    setCurrentPage(GUIDED_HIGHLIGHTING_POPUP_PAGES[0]);
  };

  return (
    <Modal
      {...modalProps}
      onBackgroundClick={() => setActive(false)}
      isActive={active && showModal}
      __cssFor={{ root: modalCss }}
      ariaProps={{
        "aria-labelledby": "popup_modal_label",
      }}
    >
      <div css={modalWrapper}>
        <div css={tittleWrapper}>
          <h2 css={modalTitle}>{currentPage?.title}</h2>
          <Button
            __cssFor={{ root: closeButtonCss }}
            flavor="INCOGNITO"
            ariaProps={{
              "aria-label": "Close Modal Button",
            }}
            icon={{ SvgComponent: CloseSVG }}
            onClick={() => closePopup()}
          />
        </div>
        <div css={modalContentWrapper}>
          <div css={modalContentImageWrapper}>
            <img
              css={modalContentImage}
              src={currentPage?.image_url}
              alt={
                currentPage?.page === 2
                  ? currentPage?.title.concat(" check illustration")
                  : currentPage?.title.concat(" illustration")
              }
            />
          </div>
          <div css={modalContentText}>
            <h1>{currentPage?.heading_text}</h1>
            <p>{currentPage?.paragraph_text}</p>
            {currentPage?.page === lastPage?.page && (
              <Checkbox
                label="Do not show again"
                name="class"
                onChange={(event) => {
                  setDoNotShowAgain(event);
                }}
                __cssFor={{ root: checkboxCss }}
              />
            )}
          </div>
        </div>
        <div css={modalPaginationWrapper}>{renderPagination()}</div>
        <div css={modalFooterCss}>
          <div css={footerButtonsWrapper}>
            {currentPage?.page !== firstPage?.page && (
              <Button
                flavor={Button.flavors.tertiary}
                onClick={() => handleBackPage()}
                ariaProps={{
                  "aria-label": "Back Button",
                }}
              >
                <Icon SvgComponent={ChevronLeftSVG} size={Icon.sizes.sm} />
                Back
              </Button>
            )}
            {currentPage?.page !== lastPage?.page && (
              <Button
                __cssFor={{ root: footerNextButton }}
                flavor={Button.flavors.primary}
                onClick={() => handleNextPage()}
                ariaProps={{
                  "aria-label": "Next Button",
                }}
              >
                Next
              </Button>
            )}
            {currentPage?.page === lastPage?.page && (
              <Button
                __cssFor={{ root: footerNextButton }}
                flavor={Button.flavors.primary}
                onClick={() => {
                  closePopup();
                  onClickGetStarted();
                }}
                ariaProps={{
                  "aria-label": "Get Started Button",
                }}
              >
                Get Started!
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GuidedHighlightingTeacherInfoPopup;
